<template>
  <Popover
    :is-open="isBasketFlyoutOpen"
    @touchstart="handleTouchStart"
    @mouseenter="handleMouseEnter"
    @touchend="handleTouchEnd"
    @mouseleave="handleMouseLeave"
  >
    <template #action>
      <Backdrop
        :show="isBasketFlyoutOpen"
        class="z-50"
        @click="closeBasketFlyout"
      />
      <AgsLink
        data-test-id="basket-link"
        data-qa="cart-icon"
        :to="toLocalePath({ name: 'basket' })"
        type="loud"
        :badge="count && count > 0 ? count : undefined"
        :class="{ 'pb-1': !isMobile }"
        :aria-label="$t('navigation.basket')"
        @click="trackNavigationHeader($t('navigation.basket'))"
      >
        <template #icon>
          <IconShoppingCart class="mt-[-2px] size-6 scale-110" />
        </template>
        <span class="mt-0 hidden text-sm normal-case lg:flex">
          {{ $t('navigation.basket') }}
        </span>
      </AgsLink>
    </template>
    <template #content>
      <div @mouseleave="handleMouseLeave">
        <ClientOnly>
          <div
            v-if="items?.length"
            class="max-h-[calc(100vh-25rem)] overflow-auto sm:max-h-fit"
          >
            <article v-for="(item, index) in groupedItems" :key="item.key">
              <Headline
                v-if="item.key === firstUnavailableKey"
                tag="h2"
                :is-uppercase="false"
                size="base"
                class="text-secondary border-gray-500 border-y px-4 py-3 font-bold"
              >
                {{ $t('basket.unavailable_products') }}
              </Headline>
              <BasketCardPopover
                is-light-variant
                :product="item.product"
                :variant="item.variant"
                :title="getBasketCardPopoverTitle(item.product.attributes)"
                :size="getBasketCardPopoverSize(item.variant)"
                :color="getProductColors(item.product, 'color').join('/')"
                :article-no="item.variant.referenceKey || ''"
                :image-hash="getFrontImage(item.product.images)?.hash || ''"
                :reduced-price="getDiscountPrice(item.price)"
                :price="item.price.total.withTax"
                :quantity="item.quantity"
                :available-quantity="item.availableQuantity"
                :show-price-reduction="isActiveSalePeriod"
                :is-sold-out="isBasketItemUnavailable(item)"
                :has-basket-action="false"
                :has-quantity-action="false"
                :has-wishlist-action="false"
                :order-number="
                  (item?.customData as BasketItemCustomData)?.ordernumber
                "
              />
              <hr
                v-if="
                  index < groupedItems.length - 1 &&
                  groupedItems[index + 1]?.key !== firstUnavailableKey
                "
                class="bg-gray-500 w-full"
              />
            </article>
          </div>
          <div
            v-if="!items?.length"
            class="flex w-full min-w-[200px] flex-col items-center justify-center py-4"
            @mouseleave="handleMouseLeave"
          >
            <div class="w-2/3">
              <p class="text-center text-sm">
                {{ $t('basket.no_items_info') }}
              </p>
            </div>
          </div>
          <BasketCTAs v-if="items?.length" @close="closeBasketFlyout" />
        </ClientOnly>
      </div>
    </template>
  </Popover>
</template>

<script setup lang="ts">
import type { Variant, Attributes, BasketItem } from '@scayle/storefront-nuxt'
import {
  getAppliedReductionsByCategory,
  getFirstAttributeValue,
  getProductColors,
} from '@scayle/storefront-nuxt'
import type { BasketItemCustomData } from '#layers/base/types/basket'
import { getSizeLabel } from '#layers/base/types/variant'
import { isBasketItemUnavailable } from '#layers/base/utils/basket'

const props = defineProps<{ items?: BasketItem[]; count?: number }>()

const { toLocalePath } = useRouteHelpers()
const { isMobile } = useDeviceType()
const { openBasketFlyout, closeBasketFlyout, isBasketFlyoutOpen } = useUiState()
const { trackNavigationHeader, trackBasket } = useTrackingEvents()
const i18n = useI18n()

const { isActiveSalePeriod } = useSmartShopConfiguration()

const getDiscountPrice = (price: BasketItem['price']) =>
  getAppliedReductionsByCategory(price.total, 'sale')[0]?.amount.absoluteWithTax

// Group available items first, then unavailable, matching the basket page. The
// "Derzeit nicht verfügbare Artikel" heading is rendered before the first
// unavailable item.
const availableItems = computed(() =>
  (props.items ?? []).filter((item) => !isBasketItemUnavailable(item)),
)
const unavailableItems = computed(() =>
  (props.items ?? []).filter((item) => isBasketItemUnavailable(item)),
)
const groupedItems = computed(() => [
  ...availableItems.value,
  ...unavailableItems.value,
])
const firstUnavailableKey = computed(() => unavailableItems.value[0]?.key)

const isTouch = ref(false)
const handleTouchStart = (e?: Event) => {
  e?.preventDefault()
  isTouch.value = true
}
const handleTouchEnd = () => {
  isTouch.value = true
}
const handleMouseEnter = () => {
  if (!isMobile.value) {
    openBasketFlyout()
    trackBasket({ basketItems: props.items ?? [] })
  }
}
const handleMouseLeave = () => {
  closeBasketFlyout()
}

const getBasketCardPopoverTitle = (attrs: Attributes | undefined) =>
  getFirstAttributeValue(attrs, 'name')?.label || ''
const getBasketCardPopoverSize = (variant: Variant) =>
  getSizeLabel(variant) || ''

const originalTitle = ref('')
let interval: ReturnType<typeof setInterval> | null = null
const parts = ref<string[]>([])
const idx = ref(0)
const totalQuantity = computed(() =>
  (props.items ?? []).reduce((sum, item) => sum + (item?.quantity ?? 0), 0),
)
const didOverwriteTitle = ref(false)
const getSharedTitleState = () => {
  if (!import.meta.client) {
    return null
  }
  const w = window as unknown as {
    __agsBasketTitleNotifier?: {
      interval: ReturnType<typeof setInterval> | null
      originalTitle: string
      didOverwriteTitle: boolean
    }
  }
  if (!w.__agsBasketTitleNotifier) {
    w.__agsBasketTitleNotifier = {
      interval: null,
      originalTitle: '',
      didOverwriteTitle: false,
    }
  }
  return w.__agsBasketTitleNotifier
}
const hasSeg =
  import.meta.client && typeof Intl !== 'undefined' && Intl.Segmenter
const segmenter = hasSeg
  ? new Intl.Segmenter('de', { granularity: 'grapheme' })
  : null

const splitGraphemes = (s: string) => {
  if (!s) {
    return []
  }
  if (segmenter) {
    return Array.from(segmenter.segment(s), (x) => x.segment)
  }
  return Array.from(s)
}

const getNotificationText = () => {
  // Never update the title if the cart is empty.
  if (totalQuantity.value <= 0) {
    return null
  }

  return totalQuantity.value > 1
    ? `   ${i18n.t('global.title_inactive_tab_plural')} `
    : `   ${i18n.t('global.title_inactive_tab_singular')} `
}

const STEP_HIDDEN = 2 // Number of graphemes per tick
const DELAY_HIDDEN = 1000

const buildParts = () => {
  const text = getNotificationText()
  parts.value = text ? splitGraphemes(text) : []
  idx.value = 0
}

const tick = () => {
  if (!import.meta.client || !parts.value.length) {
    return
  }
  const shared = getSharedTitleState()
  if (!shared) {
    return
  }
  didOverwriteTitle.value = shared.didOverwriteTitle
  originalTitle.value = shared.originalTitle
  const view = parts.value
    .slice(idx.value)
    .concat(parts.value.slice(0, idx.value))
    .join('')
  document.title = view
  shared.didOverwriteTitle = true
  didOverwriteTitle.value = true
  idx.value = (idx.value + STEP_HIDDEN) % parts.value.length
}

const startHidden = () => {
  const shared = getSharedTitleState()
  if (!shared) {
    return
  }
  interval = shared.interval
  if (interval || !parts.value.length) {
    return
  }
  // Capture the current route/page title right before we start overwriting it.
  // This component lives in the header and persists across navigations.
  shared.originalTitle = document.title
  originalTitle.value = shared.originalTitle
  tick()
  shared.interval = setInterval(tick, DELAY_HIDDEN)
  interval = shared.interval
}

const stopAllAndRestore = () => {
  const shared = getSharedTitleState()
  if (!shared) {
    return
  }
  interval = shared.interval
  originalTitle.value = shared.originalTitle
  didOverwriteTitle.value = shared.didOverwriteTitle
  if (interval) {
    clearInterval(interval)
    shared.interval = null
    interval = null
  }
  // Only restore if we actually overwrote the title (prevents stale restores
  // when cart is empty and we never started the animation).
  if (import.meta.client && didOverwriteTitle.value && originalTitle.value) {
    document.title = originalTitle.value
    shared.didOverwriteTitle = false
    didOverwriteTitle.value = false
  }
}

const onVisibilityChange = () => {
  if (!import.meta.client) {
    return
  }
  const shared = getSharedTitleState()
  if (!shared) {
    return
  }
  interval = shared.interval
  originalTitle.value = shared.originalTitle
  didOverwriteTitle.value = shared.didOverwriteTitle
  if (document.hidden && !isMobile.value && totalQuantity.value > 0) {
    // Capture the current route/page title right before we start overwriting it.
    if (!shared.didOverwriteTitle) {
      shared.originalTitle = document.title
      originalTitle.value = shared.originalTitle
    }
    buildParts()
    if (parts.value.length) {
      startHidden()
    }
  } else {
    stopAllAndRestore()
  }
}

let cartObserver: MutationObserver | null = null

onMounted(() => {
  if (!import.meta.client) {
    return
  }
  {
    const shared = getSharedTitleState()
    if (shared && !shared.originalTitle) {
      shared.originalTitle = document.title
    }
    originalTitle.value = shared?.originalTitle ?? document.title
    didOverwriteTitle.value = shared?.didOverwriteTitle ?? false
    interval = shared?.interval ?? null
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  cartObserver = new MutationObserver(() => {
    if (!document.hidden) {
      return
    }
    const hadParts = parts.value.length > 0
    buildParts()
    if (!hadParts && parts.value.length) {
      startHidden()
    }
    if (hadParts && !parts.value.length) {
      stopAllAndRestore()
    }
  })
  const badgeRoot =
    document.querySelector('a[data-test-id="basket-link"]') || document.body
  cartObserver.observe(badgeRoot, {
    subtree: true,
    childList: true,
    characterData: true,
  })
  onVisibilityChange()
})

onUnmounted(() => {
  if (!import.meta.client) {
    return
  }

  document.removeEventListener('visibilitychange', onVisibilityChange)
  stopAllAndRestore()
  cartObserver?.disconnect()
})

watch(
  () => totalQuantity.value,
  () => {
    if (!import.meta.client) {
      return
    }
    if (!document.hidden) {
      return
    }
    const shared = getSharedTitleState()
    if (!shared) {
      return
    }

    const hadParts = parts.value.length > 0
    buildParts()

    // If the cart became empty while hidden, stop immediately and restore.
    if (hadParts && !parts.value.length) {
      stopAllAndRestore()
      return
    }

    // If the cart became non-empty while hidden, start animation.
    if (!hadParts && parts.value.length) {
      if (!shared.didOverwriteTitle) {
        shared.originalTitle = document.title
        originalTitle.value = shared.originalTitle
      }
      startHidden()
    }
  },
)
</script>
